import React, { useState } from 'react';
import TimezoneSelector from './components/TimezoneSelector';

/**
 * Timezone Selector Prototype Demo Page
 *
 * Demonstrates the timezone selector component based on Nielsen Norman Group research.
 * Accessible at /proto-tz-selector
 */
export default function TimezonePrototype() {
  const [selectedTz1, setSelectedTz1] = useState('');
  const [selectedTz2, setSelectedTz2] = useState('America/New_York');
  const [selectedTz3, setSelectedTz3] = useState('');

  const handleTz1Change = (event, timezone) => {
    setSelectedTz1(timezone.id);
    console.log('Demo 1 - Selected timezone:', timezone);
  };

  const handleTz2Change = (event, timezone) => {
    setSelectedTz2(timezone.id);
    console.log('Demo 2 - Selected timezone:', timezone);
  };

  const handleTz3Change = (event, timezone) => {
    setSelectedTz3(timezone.id);
    console.log('Demo 3 - Selected timezone:', timezone);
  };

  return (
    <div style={styles.container}>
      <div style={styles.demoContainer}>
        <h1 style={styles.h1}>Timezone Selector Component</h1>
        <p style={styles.subtitle}>
          A user-friendly timezone selector based on Nielsen Norman Group research findings
        </p>

        {/* Research Highlights */}
        <div style={styles.researchHighlights}>
          <h3 style={styles.researchTitle}>Key Research Findings (NNG, December 2022)</h3>
          <ul style={styles.ul}>
            <li style={styles.li}>
              <strong>73% of users know their timezone</strong> (e.g., "Pacific Time"), but only 34% know their UTC offset
            </li>
            <li style={styles.li}>
              <strong>Most common search query: City (44%)</strong>, followed by timezone name (24%)
            </li>
            <li style={styles.li}>
              <strong>Users expect alphabetical ordering</strong>, not offset-based ordering
            </li>
            <li style={styles.li}>
              <strong>Users expect automatic location detection</strong>
            </li>
            <li style={styles.li}>
              <strong>Search functionality is critical</strong> - nearly all users try to search first
            </li>
            <li style={styles.li}>
              <strong>GMT is more familiar than UTC</strong> (even though UTC is the correct standard)
            </li>
          </ul>
        </div>

        {/* Demo Section 1: Basic Usage */}
        <div style={styles.section}>
          <h2 style={styles.h2}>Demo 1: Basic Timezone Selector</h2>
          <div style={styles.demoBox}>
            <h3 style={styles.h3}>Try it out</h3>
            <p style={styles.p}>
              This selector automatically detects your timezone and allows you to search by city, country, state, timezone name, or offset (e.g., "7" for GMT+7).
            </p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Select your timezone:</label>
              <TimezoneSelector
                onChange={handleTz1Change}
                value={selectedTz1}
                name="timezone1"
              />
            </div>

            {selectedTz1 && (
              <div style={styles.resultBox}>
                <h4 style={styles.resultTitle}>Selected Timezone:</h4>
                <pre style={styles.pre}>{selectedTz1}</pre>
              </div>
            )}
          </div>
        </div>

        {/* Demo Section 2: Pre-selected Value */}
        <div style={styles.section}>
          <h2 style={styles.h2}>Demo 2: Pre-selected Timezone</h2>
          <div style={styles.demoBox}>
            <h3 style={styles.h3}>Programmatically set timezone</h3>
            <p style={styles.p}>
              This example demonstrates setting a timezone programmatically. Try the buttons below to change it.
            </p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Timezone (pre-selected to New York):</label>
              <TimezoneSelector
                onChange={handleTz2Change}
                value={selectedTz2}
                name="timezone2"
              />
            </div>

            <div style={styles.buttonGroup}>
              <button
                style={styles.btn}
                onClick={() => setSelectedTz2('America/Los_Angeles')}
              >
                Set to Los Angeles
              </button>
              <button
                style={styles.btn}
                onClick={() => setSelectedTz2('Europe/London')}
              >
                Set to London
              </button>
              <button
                style={styles.btn}
                onClick={() => setSelectedTz2('Asia/Tokyo')}
              >
                Set to Tokyo
              </button>
            </div>

            {selectedTz2 && (
              <div style={styles.resultBox}>
                <h4 style={styles.resultTitle}>Current Timezone:</h4>
                <pre style={styles.pre}>{selectedTz2}</pre>
              </div>
            )}
          </div>
        </div>

        {/* Demo Section 3: No Auto-detect */}
        <div style={styles.section}>
          <h2 style={styles.h2}>Demo 3: Without Auto-detection</h2>
          <div style={styles.demoBox}>
            <h3 style={styles.h3}>Manual selection only</h3>
            <p style={styles.p}>
              This instance has auto-detection disabled, requiring the user to manually select their timezone.
            </p>

            <div style={styles.formGroup}>
              <label style={styles.label}>Select timezone manually:</label>
              <TimezoneSelector
                onChange={handleTz3Change}
                value={selectedTz3}
                name="timezone3"
                autoDetect={false}
                placeholder="Choose a timezone..."
              />
            </div>

            {selectedTz3 && (
              <div style={styles.resultBox}>
                <h4 style={styles.resultTitle}>Selected Timezone:</h4>
                <pre style={styles.pre}>{selectedTz3}</pre>
              </div>
            )}
          </div>
        </div>

        {/* Features Section */}
        <div style={styles.section}>
          <h2 style={styles.h2}>Features</h2>
          <div style={styles.demoBox}>
            <h3 style={styles.h3}>Implemented Features</h3>
            <ul style={styles.featureList}>
              <li><strong>Automatic timezone detection</strong> using browser API</li>
              <li><strong>Searchable dropdown</strong> - search by city, country, state, timezone name, or offset</li>
              <li><strong>Alphabetical ordering</strong> by city (as users expect)</li>
              <li><strong>Full keyboard navigation</strong> - Arrow keys, Enter, Escape, Home, End</li>
              <li><strong>Mobile-friendly</strong> - Touch-friendly tap targets (44x44px)</li>
              <li><strong>Accessible</strong> - WCAG 2.1 Level AA compliant with ARIA attributes</li>
              <li><strong>GMT offset display</strong> (research shows GMT is more familiar than UTC)</li>
              <li><strong>Real-time search filtering</strong> with 150ms debounce</li>
              <li><strong>Curated list</strong> - ~70 major cities (down from 400+ PHP timezones)</li>
              <li><strong>React hooks</strong> - Modern React implementation with hooks</li>
            </ul>
          </div>

          <div style={styles.demoBox}>
            <h3 style={styles.h3}>Keyboard Shortcuts</h3>
            <ul style={styles.featureList}>
              <li><strong>Tab</strong> - Focus search field when dropdown opens</li>
              <li><strong>Arrow Up/Down</strong> - Navigate through timezone list</li>
              <li><strong>Enter</strong> - Select highlighted timezone</li>
              <li><strong>Escape</strong> - Close dropdown</li>
              <li><strong>Home/End</strong> - Jump to first/last timezone</li>
              <li><strong>Type to search</strong> - Search field is automatically focused</li>
            </ul>
          </div>

          <div style={styles.demoBox}>
            <h3 style={styles.h3}>Search Strategies Supported</h3>
            <ul style={styles.featureList}>
              <li><strong>City</strong> - "Los Angeles", "Berlin", "Tokyo" (most common: 44%)</li>
              <li><strong>Country</strong> - "United States", "Germany", "Australia"</li>
              <li><strong>State</strong> - "California", "New York", "Queensland"</li>
              <li><strong>Timezone name</strong> - "Pacific Time", "Central European Time" (24%)</li>
              <li><strong>Abbreviations</strong> - "PST", "EST", "CET"</li>
              <li><strong>Offset</strong> - "7" (GMT+7), "-5" (GMT-5), "GMT+7", "UTC+7", "+5:30" (half-hour offsets)</li>
            </ul>
          </div>
        </div>

        {/* Usage Example */}
        <div style={styles.section}>
          <h2 style={styles.h2}>Usage Example</h2>
          <div style={styles.demoBox}>
            <h3 style={styles.h3}>React Component</h3>
            <pre style={styles.codeBlock}>{`import TimezoneSelector from './components/TimezoneSelector';

function MyForm() {
  const [timezone, setTimezone] = useState('');

  const handleTimezoneChange = (event, timezoneData) => {
    setTimezone(timezoneData.id);
    console.log('Selected:', timezoneData);
  };

  return (
    <form>
      <TimezoneSelector
        onChange={handleTimezoneChange}
        value={timezone}
        name="timezone"
        autoDetect={true}
      />
    </form>
  );
}`}</pre>
          </div>
        </div>
      </div>
    </div>
  );
}

// Inline styles for the demo page
const styles = {
  container: {
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    backgroundColor: '#f5f5f5',
    padding: '20px',
    lineHeight: 1.6,
    minHeight: '100vh',
  },
  demoContainer: {
    maxWidth: '1200px',
    margin: '0 auto',
    backgroundColor: '#ffffff',
    padding: '40px',
    borderRadius: '8px',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
  },
  h1: {
    fontSize: '32px',
    marginBottom: '16px',
    color: '#333333',
  },
  subtitle: {
    fontSize: '18px',
    color: '#666666',
    marginBottom: '32px',
  },
  researchHighlights: {
    backgroundColor: '#fff9e6',
    padding: '20px',
    borderLeft: '4px solid #ffc107',
    marginBottom: '24px',
  },
  researchTitle: {
    fontSize: '18px',
    marginBottom: '12px',
    color: '#333333',
  },
  ul: {
    listStylePosition: 'inside',
    color: '#666666',
    paddingLeft: '0',
  },
  li: {
    marginBottom: '8px',
  },
  section: {
    marginBottom: '48px',
  },
  h2: {
    fontSize: '24px',
    marginBottom: '16px',
    color: '#333333',
    borderBottom: '2px solid #e0e0e0',
    paddingBottom: '8px',
  },
  demoBox: {
    backgroundColor: '#f9f9f9',
    padding: '24px',
    borderRadius: '4px',
    border: '1px solid #e0e0e0',
    marginBottom: '24px',
  },
  h3: {
    fontSize: '18px',
    marginBottom: '16px',
    color: '#333333',
  },
  p: {
    marginBottom: '16px',
    color: '#666666',
  },
  formGroup: {
    marginBottom: '24px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontWeight: '500',
    color: '#333333',
  },
  buttonGroup: {
    display: 'flex',
    gap: '10px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  btn: {
    padding: '10px 20px',
    backgroundColor: '#0066cc',
    color: '#ffffff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    fontSize: '16px',
    transition: 'background-color 0.2s',
  },
  resultBox: {
    marginTop: '16px',
    padding: '16px',
    backgroundColor: '#e6f2ff',
    border: '1px solid #0066cc',
    borderRadius: '4px',
  },
  resultTitle: {
    fontSize: '16px',
    marginBottom: '8px',
    color: '#0066cc',
  },
  pre: {
    backgroundColor: '#ffffff',
    padding: '12px',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: 1.4,
    margin: 0,
  },
  featureList: {
    listStylePosition: 'inside',
    color: '#666666',
    paddingLeft: '0',
  },
  codeBlock: {
    backgroundColor: '#f5f5f5',
    padding: '16px',
    borderRadius: '4px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: 1.5,
    margin: 0,
  },
};
